import { Fragment, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Popover, Transition } from "@headlessui/react";
import axios from "../../api/axios";
import { PromangPowered } from "../branding/PromangPowered";

import {
  Bars3Icon,
  ExclamationTriangleIcon,
  HomeModernIcon,
  NewspaperIcon,
  ShieldCheckIcon,
  WrenchIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Link } from "react-router-dom";
import Logo from "../../components/images/LOGO.png";
// import CustomerPopover from "../utils/CustomerPopover";
// import ShoppingCart from "../utils/ShoppingCart";
import Breadcrumbs from "../utils/breadcrumbs";
import { ResourceLoading } from "../utils/ResourceLoading";
import { categoryPath } from "../utils/Utils";
import { isChristmasPeriod, getChristmasColor } from "../utils/ChristmasUtils";
import { SnowEffect } from "../utils/SnowEffect";
import { CandyCane } from "../utils/CandyCane";
import CookieConsent from "../utils/CookieConsent";
import { AnnouncementsNotice } from "../AnnouncementsNotice";
import { useCompanyInfo } from "../../data/company";

const solutions = [
  {
    name: "Balansert ventilasjon",
    description: "Prosjektering og montasje av nytt anlegg — bolig og næring.",
    href: "/tjenester/ventilasjon",
    icon: HomeModernIcon,
  },
  {
    name: "Full service",
    description: "Kontroll, rengjøring og inn-regulering av anlegget.",
    href: "/tjenester/full-service",
    icon: WrenchScrewdriverIcon,
  },
  {
    name: "Filterservice",
    description: "Kontroll av aggregat og bytte av filter — året rundt.",
    href: "/tjenester/filterservice",
    icon: ShieldCheckIcon,
  },
  {
    name: "Reparasjon",
    description: "Vi reparerer aggregat og vifter — alle modeller og merker.",
    href: "/tjenester/reparasjon",
    icon: WrenchIcon,
  },
  {
    name: "Kontroll av ventilasjon",
    description: "Tilstandskontroll og måling av anleggets ytelse.",
    href: "/tjenester/kontroll",
    icon: NewspaperIcon,
  },
  {
    name: "Radon",
    description: "Radonmåling og radonsanering i bolig og bygg.",
    href: "/tjenester/radon",
    icon: ExclamationTriangleIcon,
  },
];

const resources = [
  {
    name: "Kontakt oss",
    description: "Kontaktinformasjon, adresse og mer",
    href: "/kontakt",
  },
  { name: "Om oss", description: "Vår lille historie", href: "/om-oss" },
  { name: "Hjelp & FAQ", description: "Alt du trenger å vite", href: "/hjelp" },
  {
    name: "Innlegg",
    description: "Artikler og guider om ventilasjon og inneklima",
    href: "/innlegg",
  },
  {
    name: "Prosjekter",
    description: "Om våre prosjekter og referanser fra kunder",
    href: "/om-oss/prosjekter",
  },
  {
    name: "Ledige Stillinger",
    description: "Se ledige stillinger og søk direkte",
    href: "/stillinger/",
  },
  {
    name: "Ansatte",
    description: "Hvem er våre ansatte?",
    href: "/om-oss/ansatte",
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function HomeLayout({ children }) {
  const company = useCompanyInfo();
  const [, setState] = useState({
    isLoading: false,
    error: false,
    errors: null,
  });

  const [categories, setCategories] = useState([]);

  const [categoriesLoading, setCategoriesLoading] = useState(false);

  const getCategories = () => {
    setState({ loading: true });
    setCategoriesLoading(true);

    axios
      .get(`/products/categories/list?parents=true`)
      .then((response) => {
        setState({ loading: false, error: false });
        setCategories(response.data.data);
        setCategoriesLoading(false);
      })
      .catch((e) => {
        setState({ loading: false, error: true });
        setCategoriesLoading(false);
      });
  };

  useEffect(() => {
    setTimeout(() => {
      getCategories();
    }, 1000);
  }, []);

  const currentYear = new Date().getFullYear();

  return (
    <div className="relative min-h-full" style={{ backgroundColor: "var(--bg)" }}>
      {isChristmasPeriod() && <SnowEffect />}
      <div className="topbar">
        <div className="wrap topbar-inner">
          <div>
            <span>📍 {company.address.full}</span>
            <span className="hidden sm:inline sm:ml-5">🕒 {company.hours.short}</span>
          </div>
          <div>
            <a href={`tel:${company.phone.tel}`}>📞 {company.phone.display}</a>
            <a href={`mailto:${company.email}`}>{company.email}</a>
          </div>
        </div>
      </div>
      <div className="relative z-10">
        <Popover className="relative z-50 w-full">
          <div className="nav">
            <div className="wrap flex min-h-[72px] items-center justify-between gap-3 py-2 md:min-h-[84px] md:py-0">
            {isChristmasPeriod() && (
              <>
                {/* Left candy cane */}
                <div className="absolute left-4 top-1/2 -translate-y-1/2 opacity-70 pointer-events-none z-0">
                  <CandyCane className="h-20 w-5 md:h-24 md:w-6 drop-shadow-lg" />
                </div>
                {/* Right candy cane */}
                <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-70 pointer-events-none z-0">
                  <CandyCane className="h-20 w-5 md:h-24 md:w-6 drop-shadow-lg transform scale-x-[-1]" />
                </div>
              </>
            )}
            <div className="relative z-10 flex shrink-0 justify-start">
              <Link
                to="/"
                className="rounded-lg outline-none ring-[var(--airon)]/0 transition duration-300 hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[var(--airon)]/40"
              >
                <span className="sr-only">Airon AS</span>
                <img
                  className={
                    isChristmasPeriod()
                      ? "logo-img h-12 w-auto sm:h-16"
                      : "logo-img h-[38px] w-auto sm:h-[42px]"
                  }
                  src={isChristmasPeriod() ? "/logo-c.png" : Logo}
                  alt="Airon AS Logo"
                />
              </Link>
            </div>
            <div className="-my-2 -mr-2 md:hidden relative z-10">
              <Popover.Button className={classNames("inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset", getChristmasColor("focus:ring-indigo-500", "focus:ring-red-500"))}>
                <span className="sr-only">Åpne meny</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </Popover.Button>
            </div>
            <Popover.Group
              as="nav"
              className="nav-links relative z-10 hidden flex-1 flex-wrap items-center justify-center gap-2 md:flex md:gap-8"
            >
              <Popover className="relative rounded-xl p-2 transition-colors hover:bg-slate-50/80">
                {({ open, close }) => (
                  <>
                    <Popover.Button
                      className={classNames(
                        open ? "text-gray-900" : "text-gray-500",
                        "group inline-flex items-center rounded-lg bg-transparent px-1 text-base font-medium transition duration-200 hover:text-gray-900 focus:outline-none focus:ring-2",
                        getChristmasColor("focus:ring-blue-400", "focus:ring-red-500"),
                        "focus:ring-offset-2"
                      )}
                    >
                      <span>Tjenester</span>
                      <ChevronDownIcon
                        className={classNames(
                          open ? "text-gray-600" : "text-gray-400",
                          "ml-2 h-5 w-5 group-hover:text-gray-500"
                        )}
                        aria-hidden="true"
                      />
                    </Popover.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel className="absolute z-50 -ml-4 mt-3 w-screen max-w-md transform lg:left-1/2 lg:ml-0 lg:max-w-2xl lg:-translate-x-1/2">
                        <div className="overflow-hidden rounded-2xl shadow-soft ring-1 ring-slate-900/5">
                          <div className="relative grid gap-6 bg-white/95 px-5 py-6 backdrop-blur-sm sm:gap-8 sm:p-8 lg:grid-cols-2">
                            {solutions.map((solution) => (
                              <Link
                                key={solution.name}
                                to={solution.href}
                                onClick={() => close()}
                                className="-m-3 flex items-start rounded-xl p-3 transition duration-200 hover:bg-slate-50"
                              >
                                <div className={classNames("flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md text-white sm:h-12 sm:w-12", getChristmasColor("bg-blue-400", "bg-red-500"))}>
                                  <solution.icon
                                    className="h-6 w-6"
                                    aria-hidden="true"
                                  />
                                </div>
                                <div className="ml-4">
                                  <p className="text-base font-medium text-gray-900">
                                    {solution.name}
                                  </p>
                                  <p className="mt-1 text-sm text-gray-500">
                                    {solution.description}
                                  </p>
                                </div>
                              </Link>
                            ))}
                          </div>
                          {/* <div className="bg-gray-50 p-5 sm:p-8">
                            <a href="#" className="-m-3 flow-root rounded-md p-3 hover:bg-gray-100">
                              <div className="flex items-center">
                                <div className="text-base font-medium text-gray-900">Enterprise</div>
                                <span className="ml-3 inline-flex items-center rounded-full bg-indigo-100 px-3 py-0.5 text-xs font-medium leading-5 text-indigo-800">
                                  New
                                </span>
                              </div>
                              <p className="mt-1 text-sm text-gray-500">
                                Empower your entire team with even more advanced tools.
                              </p>
                            </a>
                          </div> */}
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>

              <Link
                to={"/produkter"}
                className="flex items-start rounded-xl p-3 text-slate-600 transition duration-200 hover:bg-slate-50 hover:text-slate-900"
              >
                Nettbutikk
              </Link>

              <Popover className="relative rounded-xl p-2 transition-colors hover:bg-slate-50/80">
                {({ open, close }) => (
                  <>
                    <Popover.Button
                      className={classNames(
                        open ? "text-gray-900" : "text-gray-500",
                        "group inline-flex items-center rounded-lg bg-transparent px-1 text-base font-medium transition duration-200 hover:text-gray-900 focus:outline-none focus:ring-2",
                        getChristmasColor("focus:ring-blue-400", "focus:ring-red-500"),
                        "focus:ring-offset-2"
                      )}
                    >
                      <span>Produkter</span>
                      <ChevronDownIcon
                        className={classNames(
                          open ? "text-gray-600" : "text-gray-400",
                          "ml-2 h-5 w-5 group-hover:text-gray-500"
                        )}
                        aria-hidden="true"
                      />
                    </Popover.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel className="absolute z-50 -ml-4 mt-3 w-screen max-w-md transform lg:left-1/2 lg:ml-0 lg:max-w-2xl lg:-translate-x-1/2">
                        <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                          <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8 lg:grid-cols-2">
                            {categories.map((category) => (
                              <Link
                                key={category.title}
                                to={categoryPath(category)}
                                onClick={() => close()}
                                className="-m-3 flex items-start rounded-lg p-3 hover:bg-gray-50"
                              >
                                <div className={classNames("flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md border-0 text-white sm:h-12 sm:w-12", getChristmasColor("border-blue-300", "border-red-400"))}>
                                  {category?.image?.id ? (
                                    <img
                                      src={category?.image?.file?.thumb?.url}
                                      alt={category.title}
                                    />
                                  ) : category.icon ? (
                                    <category.icon
                                      className="h-6 w-6"
                                      aria-hidden="true"
                                    />
                                  ) : null}
                                </div>
                                <div className="ml-4">
                                  <p className="text-base font-medium text-gray-900">
                                    {category.title}
                                  </p>
                                  <p className="mt-1 text-sm text-gray-500">
                                    {category.description}
                                  </p>
                                </div>
                              </Link>
                            ))}
                            <Link
                              to="/produkter"
                              onClick={() => close()}
                              className={classNames("text-base font-medium hover:border-b-2", getChristmasColor("text-blue-400 hover:border-blue-400", "text-red-500 hover:border-red-500"))}
                            >
                              Se alle produkter
                              <span aria-hidden="true"> &rarr;</span>
                            </Link>
                          </div>
                          {/* <div className="bg-gray-50 p-5 sm:p-8">
                            <a href="#" className="-m-3 flow-root rounded-md p-3 hover:bg-gray-100">
                              <div className="flex items-center">
                                <div className="text-base font-medium text-gray-900">Enterprise</div>
                                <span className="ml-3 inline-flex items-center rounded-full bg-indigo-100 px-3 py-0.5 text-xs font-medium leading-5 text-indigo-800">
                                  New
                                </span>
                              </div>
                              <p className="mt-1 text-sm text-gray-500">
                                Empower your entire team with even more advanced tools.
                              </p>
                            </a>
                          </div> */}
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>

              <Popover className="relative rounded-xl p-2 transition-colors hover:bg-slate-50/80">
                {({ open, close }) => (
                  <>
                    <Popover.Button
                      className={classNames(
                        open ? "text-gray-900" : "text-gray-500",
                        "group inline-flex items-center rounded-lg bg-transparent px-1 text-base font-medium transition duration-200 hover:text-gray-900 focus:outline-none focus:ring-2",
                        getChristmasColor("focus:ring-blue-400", "focus:ring-red-500"),
                        "focus:ring-offset-2"
                      )}
                    >
                      <span>Om oss</span>
                      <ChevronDownIcon
                        className={classNames(
                          open ? "text-gray-600" : "text-gray-400",
                          "ml-2 h-5 w-5 group-hover:text-gray-500"
                        )}
                        aria-hidden="true"
                      />
                    </Popover.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="opacity-0 translate-y-1"
                      enterTo="opacity-100 translate-y-0"
                      leave="transition ease-in duration-150"
                      leaveFrom="opacity-100 translate-y-0"
                      leaveTo="opacity-0 translate-y-1"
                    >
                      <Popover.Panel className="absolute left-1/2 z-50 mt-3 w-screen max-w-xs -translate-x-1/2 transform px-2 sm:px-0">
                        <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                          <div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
                            {resources.map((resource) => (
                              <Link
                                key={resource.name}
                                to={resource.href}
                                onClick={() => close()}
                                className="-m-3 block rounded-md p-3 hover:bg-gray-50"
                              >
                                <p className="text-base font-medium text-gray-900">
                                  {resource.name}
                                </p>
                                <p className="mt-1 text-sm text-gray-500">
                                  {resource.description}
                                </p>
                              </Link>
                            ))}
                          </div>
                        </div>
                      </Popover.Panel>
                    </Transition>
                  </>
                )}
              </Popover>
            </Popover.Group>

            <div className="nav-cta relative z-10 flex shrink-0 items-center justify-end gap-2 md:gap-3">
              <span className="phone-mini hidden lg:inline-flex">Ledig kapasitet</span>
              <Link to="/kontakt" className="btn hidden sm:inline-flex">
                Be om tilbud <span className="arr">→</span>
              </Link>
              {/* Midlertidig skjult — aktiveres igjen når nettbutikken/min side åpnes.
              <ShoppingCart />
              <CustomerPopover />
              */}
              {/* <a onClick={() => toast.error('Denne funksjonen kommer snart!')} className="cursor-pointer whitespace-nowrap text-base font-medium text-gray-500 hover:text-gray-900">
                Logg inn
              </a>
              <a
                onClick={() => toast.error('Denne funksjonen kommer snart!')}
                className="cursor-pointer  ml-8 inline-flex items-center justify-center whitespace-nowrap rounded-md border border-transparent bg-blue-400 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700"
              >
                Registrer deg
              </a> */}
            </div>
          </div>
          </div>

          <Transition
            as={Fragment}
            enter="duration-200 ease-out"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="duration-100 ease-in"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Popover.Panel
              focus
              className="absolute z-50 inset-x-0 top-0 origin-top-right transform p-2 transition md:hidden"
            >
              {({ close }) => (
              <div className="divide-y-2 divide-gray-50 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                <div className="px-5 pt-5 pb-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <img className={isChristmasPeriod() ? "h-12 w-auto" : "h-8 w-auto"} src={isChristmasPeriod() ? "/logo-c.png" : Logo} alt="Airon AS" />
                    </div>
                    <div className="-mr-2">
                      <Popover.Button className={classNames("inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset", getChristmasColor("focus:ring-indigo-500", "focus:ring-red-500"))}>
                        <span className="sr-only">Close menu</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </Popover.Button>
                    </div>
                  </div>

                  <div className="mt-6">
                    <Link
                      onClick={() => close()}
                      to="/produkter"
                      className={classNames("flex w-full items-center justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm", getChristmasColor("bg-blue-400 hover:bg-blue-700", "bg-red-500 hover:bg-red-700"))}
                    >
                      Produkter
                    </Link>
                  </div>
                  <div className="mt-6">
                    {categoriesLoading ? (
                      <ResourceLoading />
                    ) : (
                      <nav className="grid grid-cols-1 gap-7">
                        {solutions.map((solution) => (
                          <Link
                            key={solution.name}
                            to={solution.href}
                            onClick={() => close()}
                            className="-m-3 flex items-center rounded-lg p-3 hover:bg-gray-50"
                          >
                            <div className={classNames("flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md text-white", getChristmasColor("bg-blue-400", "bg-red-500"))}>
                              <solution.icon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </div>
                            <div className="ml-4 text-base font-medium text-gray-900">
                              {solution.name}
                            </div>
                          </Link>
                        ))}
                      </nav>
                    )}
                  </div>
                </div>
                <div className="py-6 px-5">
                  <div className="grid grid-cols-2 gap-4">
                    {resources.map((resource) => (
                      <Link
                        key={resource.name}
                        to={resource.href}
                        onClick={() => close()}
                        className="text-base font-medium text-gray-900 hover:text-gray-700"
                      >
                        {resource.name}
                      </Link>
                    ))}
                  </div>
                  {/* <div className="mt-6">
                    <Link
                      href="#"
                      className="flex w-full items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700"
                    >
                      Registrer deg
                    </Link>
                    <p className="mt-6 text-center text-base font-medium text-gray-500">
                      Eksisterende kunde?{" "}
                      <Link
                        href="#"
                        className="text-indigo-600 hover:text-indigo-500"
                      >
                        Logg inn
                      </Link>
                    </p>
                  </div> */}
                </div>
              </div>
              )}
            </Popover.Panel>
          </Transition>
        </Popover>
      </div>

      <motion.main
        className="relative isolate h-full"
        initial={{ opacity: 0.96, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <Breadcrumbs />
        {children}
      </motion.main>

      <footer>
        <div className="wrap">
          <div className="foot-grid">
            <div className="foot-about">
              <div className="foot-logo">
                <img src={Logo} alt="Airon AS" />
              </div>
              <p>
                Airon AS er et lokalt ventilasjonsfirma fra Grålum i Sarpsborg.
                Vi prosjekterer, installerer og vedlikeholder balansert
                ventilasjon for bolig, borettslag og næringsbygg — med service,
                kanalrens og filterbytte året rundt.
              </p>
              <p style={{ fontFamily: "var(--mono)", fontSize: "12px", color: "#8da5bc", marginTop: 12 }}>
                {company.address.full}
              </p>
            </div>
            <div>
              <h5>Tjenester</h5>
              <Link to="/tjenester/ventilasjon">Balansert ventilasjon</Link>
              <Link to="/tjenester/full-service">Full service</Link>
              <Link to="/tjenester/filterservice">Filterservice</Link>
              <Link to="/tjenester/reparasjon">Reparasjon</Link>
              <Link to="/tjenester/kontroll">Kontroll av ventilasjon</Link>
              <Link to="/tjenester/radon">Radon</Link>
            </div>
            <div>
              <h5>Selskap</h5>
              <Link to="/om-oss">Om oss</Link>
              <Link to="/om-oss/ansatte">Ansatte</Link>
              <Link to="/om-oss/prosjekter">Prosjekter</Link>
              <Link to="/stillinger">Karriere</Link>
              <Link to="/hjelp">Hjelp &amp; FAQ</Link>
              <Link to="/innlegg">Innlegg</Link>
              <Link to="/salgsbetingelser">Salgsbetingelser</Link>
            </div>
            <div>
              <h5>Kontakt</h5>
              <a href={`tel:${company.phone.tel}`}>{company.phone.display}</a>
              <a href={`mailto:${company.email}`}>{company.email}</a>
              <Link to="/kontakt">Kontaktskjema</Link>
              <span
                style={{
                  display: "block",
                  marginTop: 12,
                  color: "#8da5bc",
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                {company.hours.lines.map((h, i) => (
                  <span key={i} style={{ display: "block" }}>
                    <span style={{ color: "#D6E0EA" }}>{h.day}</span> · {h.time}
                  </span>
                ))}
              </span>
            </div>
          </div>
          <div className="foot-bottom">
            <span>
              © {currentYear} {company.name} · Org. ventilasjon siden 2019
            </span>
            <PromangPowered variant="dark" />
            <div className="flex gap-4">
              <a
                href={company.social.facebook}
                target="_blank"
                rel="noreferrer"
                className="text-[#8da5bc] hover:text-[var(--airon)]"
                aria-label="Facebook"
              >
                <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.258 1H2.242C1.556 1 1 1.556 1 2.242v20.016c0 .686.556 1.242 1.242 1.242h10.776v-8.713h-2.932V11.39h2.932V8.887c0-2.906 1.775-4.489 4.367-4.489 1.242 0 2.31.093 2.62.134v3.037l-1.797.001c-1.41 0-1.683.67-1.683 1.653v2.168h3.362l-.438 3.396h-2.924V23.5h5.733c.686 0 1.242-.556 1.242-1.242V2.242C23.5 1.556 22.944 1 22.258 1" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
      <CookieConsent />
      <AnnouncementsNotice />
    </div>
  );
}
